import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useRef, useState } from 'react';
import { ILeaseKind, ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import * as styles from './styles';
import useFetch from 'use-http';
import LesseePickerModal from './LesseePickerModal';
import CondensedPersonnel from 'src/models/personnel/CondensedPersonnel';
import { formatCalendarDate } from 'src/util/formatCalendarDate';
import { ILeaseReminder } from 'src/models/dlcLease';
import LeaseReminderModal from './LeaseReminderModal';
import LeasePacketModal from './LeasePacketModal';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface ILeaseHeaderSectionProps {
    leaseMaster: ILeaseMaster;
    isDirty?: boolean;
    onSave?: () => Promise<boolean>;
    onAutoSave?: () => Promise<boolean>;
    isSaving?: boolean;
    saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
    onChange?: (field: string, value: unknown) => void;
}

const LeaseHeaderSection: React.FC<ILeaseHeaderSectionProps> = ({ leaseMaster, isDirty, onSave, onAutoSave, isSaving, saveStatus, onChange }) => {
    const isEditable = !!onChange;

    const { get, response } = useFetch();
    const [leaseKinds, setLeaseKinds] = useState<ILeaseKind[]>([]);
    const [isLesseePickerOpen, setIsLesseePickerOpen] = useState(false);
    const [isLandmanSearchOpen, setIsLandmanSearchOpen] = useState(false);
    const [showSavedStatus, setShowSavedStatus] = useState(false);
    const [isReminderOpen, setIsReminderOpen] = useState(false);
    const [reminders, setReminders] = useState<ILeaseReminder[]>(leaseMaster.reminders || []);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    useEffect(() => {
        setReminders(leaseMaster.reminders || []);
    }, [leaseMaster.lsNo, leaseMaster.reminders]);

    const activeReminderCount = reminders.filter(
        (reminder) => !!reminder.remindDate && String(reminder.remindDate).slice(0, 10) >= new Date().toLocaleDateString('en-CA')
    ).length;
    const [isLeasePacketOpen, setIsLeasePacketOpen] = useState(false);
    const [leasePacketCount, setLeasePacketCount] = useState(leaseMaster.leasePacket === 'Lease Packet' ? 1 : 0);

    // --- NEW: Simple flag to track if the user has actively typed ---
    const userModifiedLmNo = useRef(false);

    // Fetch lease kinds data ONCE when the component mounts
    useEffect(() => {
        const loadLeaseKinds = async () => {
            const kindsData = await get('/v1.0/leases/master/lease-kinds');
            if (response.ok && Array.isArray(kindsData)) {
                setLeaseKinds(kindsData);
            }
        };

        loadLeaseKinds();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- 2-Second Debounce Effect (ONLY fires if user actively typed) ---
    useEffect(() => {
        if (!isEditable || !userModifiedLmNo.current) {
            return undefined;
        }

        const timeoutId = setTimeout(async () => {
            if (leaseMaster.lmNo) {
                const data = await get(`/v1.0/leases/landman-name/${leaseMaster.lmNo}`);
                if (response.ok && data) {
                    handleChange('landmanName', data.landmanName);
                } else {
                    handleChange('landmanName', '');
                }
            } else {
                handleChange('landmanName', '');
            }
        }, 2000); // 2 second delay

        return () => clearTimeout(timeoutId); // Clears if user keeps typing
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaseMaster.lmNo]); // Standard React flow: only triggers when the parent state finishes updating

    useEffect(() => {
        if (saveStatus !== 'saved' || isDirty) {
            setShowSavedStatus(false);
            return undefined;
        }

        setShowSavedStatus(true);
        const timeoutId = window.setTimeout(() => setShowSavedStatus(false), 10000);
        return () => window.clearTimeout(timeoutId);
    }, [isDirty, saveStatus]);

    useEffect(() => {
        setLeasePacketCount(leaseMaster.leasePacket === 'Lease Packet' ? 1 : 0);
        setIsLeasePacketOpen(false);
    }, [leaseMaster.lsNo, leaseMaster.leasePacket]);

    const print = async (): Promise<void> => {
        if (!leaseMaster.lsNo) {
            alert('Lease number is required before running the Lease Purchase Report.');
            return;
        }

        try {
            setRequestError(null);
            await get(`/v1.0/leases/master/lease-purchase/report?lsNo=${leaseMaster.lsNo}`);
            if (!response.ok) {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to generate Lease Purchase Report.' }, response.status));
                return;
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${leaseMaster.lsNo}-LeasePurchaseReport.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error generating Lease Purchase Report.' }, 500));
        }
    };

    const pick = () => {
        setIsLesseePickerOpen(true);
    };

    const handleLesseeSelect = async (lessee: string): Promise<boolean> => {
        handleChange('lessee', lessee);
        return (await onAutoSave?.()) ?? true;
    };

    const postInvoices = () => {
        alert('TODO Post Invoices');
    };

    const openLandmanSearch = () => {
        setIsLandmanSearchOpen(true);
    };

    const handleLandmanSelect = async (landman: ILandmanSearchResult): Promise<void> => {
        userModifiedLmNo.current = false;
        handleChange('lmNo', landman.lmNo);
        handleChange('landmanName', landman.name);
        const wasSaved = (await onAutoSave?.()) ?? true;
        if (wasSaved) {
            setIsLandmanSearchOpen(false);
        }
    };

    const handleChange = (field: string, value: unknown) => {
        if (onChange) {
            onChange(field, value);
        }
    };

    const handleNumberChange = (field: string, value: string) => {
        if (field === 'lmNo') {
            userModifiedLmNo.current = true; // Mark that the user actually typed
        }
        const numValue = value === '' ? null : parseInt(value, 10);
        handleChange(field, numValue);
    };

    const getSourceDisplay = () => {
        if (leaseMaster.kind === 'K') {
            return 'KUDU';
        }
        return leaseMaster.source || 'Lease Records';
    };

    const formatDate = (date?: Date | string): string => {
        if (!date) {
            return '—';
        }
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(dateObj.getTime())) {
            return '—';
        }
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        }).format(dateObj);
    };

    const formatAcreage = (value?: number): string => {
        if (value === undefined || value === null || isNaN(Number(value))) {
            return '';
        }

        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 7,
            maximumFractionDigits: 7,
        }).format(Number(value));
    };

    // Compact grid using 24 columns for fine-grained control
    const gridLayout: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(24, 1fr)',
        gap: '0.375rem 0.5rem',
        alignItems: 'end',
    };

    return (
        <div style={styles.sectionContainer}>
            <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        fontSize: styles.typography.fontSize.lg,
                        fontWeight: styles.typography.fontWeight.semibold,
                        color: styles.colors.gray900,
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                    </svg>
                    Lease Information
                </span>
                {onSave && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {saveStatus === 'saving' && (
                            <span style={{ color: styles.colors.primary, fontSize: styles.typography.fontSize.xs }}>Autosave pending...</span>
                        )}
                        {showSavedStatus && (
                            <span style={{ color: styles.colors.success, fontSize: styles.typography.fontSize.xs }}>All changes saved</span>
                        )}
                        {saveStatus === 'error' && (
                            <span role="alert" style={{ color: styles.colors.danger, fontSize: styles.typography.fontSize.xs }}>
                                Autosave failed
                            </span>
                        )}
                        {isDirty && saveStatus !== 'saving' && saveStatus !== 'error' && (
                            <span
                                style={{
                                    color: styles.colors.warning,
                                    fontSize: styles.typography.fontSize.xs,
                                    fontWeight: styles.typography.fontWeight.medium,
                                }}
                            >
                                Unsaved
                            </span>
                        )}
                        <button
                            onClick={onSave}
                            disabled={!isDirty || isSaving}
                            style={{
                                ...styles.buttonSuccess,
                                opacity: isDirty && !isSaving ? 1 : 0.6,
                                cursor: isDirty && !isSaving ? 'pointer' : 'not-allowed',
                                padding: '0.3rem 0.75rem',
                                fontSize: styles.typography.fontSize.xs,
                            }}
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </div>

            <div style={gridLayout}>
                {/* Row 1: Doc# | Type | Project# | Prospect# | Section# | Source | Landman# | Landman Name */}
                <Field style={{ gridColumn: 'span 2' }} label="Doc #" value={leaseMaster.lsNo?.toString() || '—'} />
                <SelectField
                    style={{ gridColumn: 'span 3' }}
                    label="Type"
                    value={leaseMaster.kind || ''}
                    editable={isEditable}
                    onChange={(value) => handleChange('kind', value)}
                    options={leaseKinds.map((kind) => ({ value: kind.code, label: kind.dscr }))}
                />
                <Field
                    style={{ gridColumn: 'span 2' }}
                    label="Client #"
                    value={leaseMaster.clNo?.toString() || ''}
                    type="number"
                    editable={isEditable}
                    onChange={(value) => handleNumberChange('clNo', value)}
                />
                <Field
                    style={{ gridColumn: 'span 2' }}
                    label="Project #"
                    value={leaseMaster.prNo?.toString() || ''}
                    type="number"
                    editable={isEditable}
                    onChange={(value) => handleNumberChange('prNo', value)}
                />
                <Field
                    style={{ gridColumn: 'span 2' }}
                    label="Section #"
                    value={leaseMaster.scNo?.toString() || ''}
                    type="number"
                    editable={isEditable}
                    onChange={(value) => handleNumberChange('scNo', value)}
                />
                <Field style={{ gridColumn: 'span 3' }} label="Source" value={getSourceDisplay()} />
                <Field
                    style={{ gridColumn: 'span 2' }}
                    label="LM #"
                    value={leaseMaster.lmNo?.toString() || ''}
                    type="number"
                    editable={isEditable}
                    onChange={(value) => handleNumberChange('lmNo', value)}
                />
                <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
                    <ActionButton
                        label="Search"
                        onClick={() => {
                            openLandmanSearch();
                        }}
                        disabled={!isEditable}
                    />
                </div>
                <Field style={{ gridColumn: 'span 6' }} label="Landman" value={leaseMaster.landmanName?.toString() || ''} editable={false} />

                <Field style={{ gridColumn: 'span 2' }} label="Gross" value={formatAcreage(leaseMaster.gross)} />
                <Field style={{ gridColumn: 'span 2' }} label="Net" value={formatAcreage(leaseMaster.net)} />

                {/* Row 2: Lessee | Pick | Lessor | Acct Status | Title Status | Accts Gross | Net */}
                <Field
                    style={{ gridColumn: 'span 6' }}
                    label="Lessee"
                    value={leaseMaster.lessee || ''}
                    editable={isEditable}
                    onChange={(value) => handleChange('lessee', value)}
                />
                <div style={{ gridColumn: 'span 1', display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
                    <ActionButton label="Pick" onClick={pick} />
                </div>
                <Field
                    style={{ gridColumn: 'span 7' }}
                    label="Lessor"
                    value={leaseMaster.lessorName || '—'}
                    valueStyle={{ fontWeight: styles.typography.fontWeight.bold }}
                />
                <Field style={{ gridColumn: 'span 2' }} label="Inv. Paid" value={formatDate(leaseMaster.invoicePaid)} />
                <div style={{ gridColumn: 'span 1', display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
                    <ActionButton label="Post Inv." onClick={postInvoices} />
                </div>
                <div style={{ gridColumn: 'span 1', display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
                    <ActionButton
                        label="Print"
                        onClick={() => {
                            void print();
                        }}
                    />
                </div>
                <Field style={{ gridColumn: 'span 2' }} label="Acct Status" value={leaseMaster.acctStatus || 'N/A'} />
                <Field style={{ gridColumn: 'span 3' }} label="Title Status" value={leaseMaster.titleStatus || 'N/A'} />

                {/* Row 3: Created By | Created On | Modified By | Mod On | Check In | CALMS IH | Packet | Reminder | Clauses | Void */}
                <Field style={{ gridColumn: 'span 3' }} label="Created By" value={leaseMaster.createBy || leaseMaster.landmanName || '—'} />
                <Field style={{ gridColumn: 'span 3' }} label="Created On" value={formatDate(leaseMaster.createDt)} />
                <Field style={{ gridColumn: 'span 3' }} label="Modified By" value={leaseMaster.modBy || '—'} />
                <Field style={{ gridColumn: 'span 3' }} label="Mod. On" value={formatDate(leaseMaster.modDt)} />
                <Field style={{ gridColumn: 'span 3' }} label="Check In Date" value={formatDate(leaseMaster.checkInDate)} />
                <Field style={{ gridColumn: 'span 3' }} label="CALMS IH Date" value={formatCalendarDate(leaseMaster.calmsIhDate)} />
                <Field style={{ gridColumn: 'span 2' }} label="Clauses" value={leaseMaster.clausesChecked || 'No'} />
                <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
                    <ActionButton
                        label={leasePacketCount > 0 ? 'Files Attached' : 'No Files'}
                        active={leasePacketCount > 0}
                        onClick={() => setIsLeasePacketOpen(true)}
                    />
                </div>
                <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
                    <ActionButton
                        label={activeReminderCount > 0 ? `${activeReminderCount} Reminder${activeReminderCount === 1 ? '' : 's'}` : 'No Reminder'}
                        active={activeReminderCount > 0}
                        onClick={() => setIsReminderOpen(true)}
                    />
                </div>
                <div style={{ gridColumn: 'span 1', display: 'flex', alignItems: 'flex-end', gap: '0.25rem', paddingBottom: '1px' }}>
                    <CheckboxField label="Void?" checked={leaseMaster.void === 'Y'} />
                </div>
            </div>

            <LesseePickerModal
                isOpen={isLesseePickerOpen}
                onClose={() => setIsLesseePickerOpen(false)}
                onSelect={handleLesseeSelect}
                currentValue={leaseMaster.lessee}
                clientNumber={leaseMaster.clNo}
            />
            <LandmanSearchModal
                isOpen={isLandmanSearchOpen}
                onClose={() => setIsLandmanSearchOpen(false)}
                onSelect={handleLandmanSelect}
                initialLmNo={leaseMaster.lmNo}
                initialName={leaseMaster.landmanName}
            />
            {leaseMaster.lsNo && (
                <LeaseReminderModal
                    isOpen={isReminderOpen}
                    leaseNumber={leaseMaster.lsNo}
                    reminders={reminders}
                    onClose={() => setIsReminderOpen(false)}
                    onRemindersChanged={setReminders}
                />
            )}
            <LeasePacketModal
                isOpen={isLeasePacketOpen}
                leaseNumber={leaseMaster.lsNo}
                onClose={() => setIsLeasePacketOpen(false)}
                onCountChange={setLeasePacketCount}
            />
        </div>
    );
};

// Helper Components

interface ILandmanSearchResult {
    lmNo: number;
    tsdNo?: number;
    firstName?: string;
    lastName?: string;
    name: string;
    city?: string;
    state?: string;
}

const LandmanSearchModal = ({
    isOpen,
    onClose,
    onSelect,
    initialLmNo,
    initialName,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (landman: ILandmanSearchResult) => Promise<void>;
    initialLmNo?: number;
    initialName?: string;
}) => {
    const { get, response } = useFetch();
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<ILandmanSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const term = initialLmNo ? initialLmNo.toString() : (initialName || '').trim();
        setSearchTerm(term);
        setResults([]);
        setMessage('');

        if (term) {
            void runSearch(term);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialLmNo, initialName]);

    const buildName = (firstName?: string, lastName?: string, fallback?: string): string => {
        const name = `${firstName || ''} ${lastName || ''}`.trim();
        return name || fallback || '';
    };

    const mapCondensedPersonnel = (person: CondensedPersonnel): ILandmanSearchResult => ({
        lmNo: person.lmNo,
        tsdNo: person.tsdNo,
        firstName: person.firstName,
        lastName: person.lastName,
        name: buildName(person.firstName, person.lastName, person.corporationName),
        city: person.city,
        state: person.state,
    });

    const mapPersonnel = (person: {
        lmNo?: number;
        tsdNo?: number;
        fName?: string;
        lName?: string;
        city?: string;
        state?: string;
        corpName?: string;
    }): ILandmanSearchResult | null => {
        if (!person || !person.lmNo) {
            return null;
        }

        return {
            lmNo: person.lmNo,
            tsdNo: person.tsdNo,
            firstName: person.fName,
            lastName: person.lName,
            name: buildName(person.fName, person.lName, person.corpName),
            city: person.city,
            state: person.state,
        };
    };

    const runSearch = async (term: string): Promise<void> => {
        const trimmedTerm = term.trim();
        if (!trimmedTerm) {
            setResults([]);
            setMessage('Enter an LM # or landman name.');
            return;
        }

        setIsSearching(true);
        setMessage('');

        if (!isNaN(Number(trimmedTerm))) {
            const personnel = await get(`v1.0/landmen/personnel/${parseInt(trimmedTerm, 10)}/get-by-lm-no`);
            if (response.ok) {
                const result = mapPersonnel(personnel);
                setResults(result ? [result] : []);
                setMessage(result ? '' : 'No landman found for that LM #.');
            } else {
                setResults([]);
                setMessage('No landman found for that LM #.');
            }
        } else {
            const searchResults = await get(`v1.0/landmen/personnel/search-by-category?term=${encodeURIComponent(trimmedTerm)}`);
            if (response.ok && Array.isArray(searchResults)) {
                const mappedResults = searchResults.map((person: CondensedPersonnel) => mapCondensedPersonnel(person));
                setResults(mappedResults);
                setMessage(mappedResults.length ? '' : 'No landmen matched that search.');
            } else {
                setResults([]);
                setMessage('Unable to search landmen right now.');
            }
        }

        setIsSearching(false);
    };

    const handleSearch = (): void => {
        void runSearch(searchTerm);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={modalStyles.backdrop} onClick={onClose}>
            <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
                <div style={modalStyles.header}>
                    <span style={modalStyles.title}>Find Landman</span>
                    <button type="button" onClick={onClose} style={modalStyles.closeButton}>
                        X
                    </button>
                </div>

                <div style={modalStyles.content}>
                    <div style={modalStyles.searchRow}>
                        <LeaseRecordInput
                            type="text"
                            value={searchTerm}
                            autoFocus={true}
                            placeholder="LM # or landman name"
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyDown}
                            style={{ ...styles.inputBase, flex: 1 }}
                        />
                        <ActionButton label={isSearching ? 'Searching...' : 'Search'} onClick={handleSearch} disabled={isSearching} />
                    </div>

                    <div style={modalStyles.results}>
                        {message && <div style={modalStyles.message}>{message}</div>}
                        {results.map((landman) => (
                            <button
                                type="button"
                                key={`${landman.lmNo}-${landman.tsdNo || ''}`}
                                onClick={() => void onSelect(landman)}
                                style={modalStyles.resultRow}
                            >
                                <span style={modalStyles.resultNumber}>{landman.lmNo}</span>
                                <span style={modalStyles.resultName}>{landman.name || 'Unnamed Landman'}</span>
                                <span style={modalStyles.resultMeta}>
                                    {landman.tsdNo ? `TSD ${landman.tsdNo}` : ''}
                                    {landman.city || landman.state
                                        ? ` ${landman.city || ''}${landman.city && landman.state ? ', ' : ''}${landman.state || ''}`
                                        : ''}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const modalStyles: { [key: string]: React.CSSProperties } = {
    backdrop: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    container: {
        width: '620px',
        maxWidth: '95vw',
        maxHeight: '80vh',
        backgroundColor: styles.colors.white,
        borderRadius: '8px',
        boxShadow: '0 20px 35px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.875rem 1rem',
        borderBottom: `1px solid ${styles.colors.gray200}`,
    },
    title: {
        fontSize: styles.typography.fontSize.lg,
        fontWeight: styles.typography.fontWeight.semibold,
        color: styles.colors.gray900,
    },
    closeButton: {
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        color: styles.colors.gray600,
        fontSize: styles.typography.fontSize.base,
        fontWeight: styles.typography.fontWeight.bold,
        lineHeight: 1,
    },
    content: {
        padding: '1rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
    },
    searchRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    results: {
        border: `1px solid ${styles.colors.gray200}`,
        borderRadius: '6px',
        overflowY: 'auto',
        maxHeight: '48vh',
        backgroundColor: styles.colors.white,
    },
    message: {
        padding: '1rem',
        color: styles.colors.gray600,
        fontSize: styles.typography.fontSize.sm,
    },
    resultRow: {
        width: '100%',
        border: 'none',
        borderBottom: `1px solid ${styles.colors.gray200}`,
        backgroundColor: styles.colors.white,
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '90px 1fr 190px',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 0.875rem',
        textAlign: 'left',
        color: styles.colors.gray900,
    },
    resultNumber: {
        fontWeight: styles.typography.fontWeight.semibold,
        color: styles.colors.primary,
        fontSize: styles.typography.fontSize.sm,
    },
    resultName: {
        fontSize: styles.typography.fontSize.sm,
        color: styles.colors.gray900,
    },
    resultMeta: {
        fontSize: styles.typography.fontSize.xs,
        color: styles.colors.gray600,
        textAlign: 'right',
    },
};

const Field = ({
    label,
    value,
    type = 'text',
    editable = false,
    onChange,
    style,
    labelButton,
    highlight = false,
    hideLabel = false,
    valueStyle,
}: {
    label: string;
    value: string;
    type?: string;
    editable?: boolean;
    onChange?: (value: string) => void;
    style?: React.CSSProperties;
    labelButton?: {
        text: string;
        onClick: () => void;
    };
    highlight?: boolean;
    hideLabel?: boolean;
    valueStyle?: React.CSSProperties;
}) => (
    <div style={{ ...styles.fieldContainer, ...style }}>
        {!hideLabel && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={styles.fieldLabel}>{label}</label>
                {labelButton && (
                    <button
                        type="button"
                        onClick={labelButton.onClick}
                        style={{
                            padding: '2px 8px',
                            fontSize: '0.75rem',
                            borderRadius: '4px',
                            border: `1px solid ${styles.colors.gray300}`,
                            backgroundColor: styles.colors.gray100,
                            cursor: 'pointer',
                        }}
                    >
                        {labelButton.text}
                    </button>
                )}
            </div>
        )}
        {editable ? (
            <LeaseRecordInput
                type={type}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                autoComplete="off"
                onFocus={(e) => {
                    e.target.style.borderColor = styles.colors.borderFocus;
                    e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = styles.colors.gray300;
                    e.target.style.boxShadow = 'none';
                }}
                style={{ ...styles.inputBase, ...valueStyle }}
            />
        ) : (
            <div
                style={{
                    ...styles.inputBase,
                    backgroundColor: highlight ? styles.colors.warning : styles.colors.gray50,
                    color: highlight ? styles.colors.gray900 : styles.colors.gray800,
                    cursor: 'default',
                    pointerEvents: 'none',
                    ...valueStyle,
                }}
            >
                {value || '\u00A0'}
            </div>
        )}
    </div>
);

const SelectField = ({
    label,
    value,
    options,
    editable = false,
    onChange,
    style,
}: {
    label: string;
    value: string;
    options: { value: string; label: string }[];
    editable?: boolean;
    onChange?: (value: string) => void;
    style?: React.CSSProperties;
}) => (
    <div style={{ ...styles.fieldContainer, ...style }}>
        <label style={styles.fieldLabel}>{label}</label>
        {editable ? (
            <select
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                autoComplete="off"
                onFocus={(e) => {
                    e.target.style.borderColor = styles.colors.borderFocus;
                    e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = styles.colors.gray300;
                    e.target.style.boxShadow = 'none';
                }}
                style={styles.selectBase}
            >
                <option value="">Select...</option>
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        ) : (
            <div
                style={{
                    ...styles.inputDisabled,
                    backgroundColor: styles.colors.gray50,
                }}
            >
                {options.find((opt) => opt.value === value)?.label || value || '—'}
            </div>
        )}
    </div>
);

const CheckboxField = ({
    label,
    checked,
    editable = false,
    onChange,
}: {
    label: string;
    checked: boolean;
    editable?: boolean;
    onChange?: (checked: boolean) => void;
}) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        <label
            style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                cursor: editable ? 'pointer' : 'default',
            }}
        >
            <LeaseRecordInput
                type="checkbox"
                checked={checked}
                disabled={!editable}
                onChange={(e) => onChange?.(e.target.checked)}
                style={
                    {
                        width: '14px',
                        height: '14px',
                        cursor: editable ? 'pointer' : 'default',
                        marginRight: '0.25rem',
                        accentColor: styles.colors.primary,
                    } as React.CSSProperties
                }
            />
            <span
                style={{
                    fontSize: styles.typography.fontSize.xs,
                    color: styles.colors.gray700,
                    fontWeight: styles.typography.fontWeight.medium,
                }}
            >
                {label}
            </span>
        </label>
    </div>
);

export const ActionButton = ({
    onClick,
    label,
    active = false,
    disabled = false,
    title,
}: {
    onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    label: string;
    active?: boolean;
    disabled?: boolean;
    title?: string;
}) => (
    <button
        title={title}
        aria-label={title}
        style={{
            ...styles.buttonSecondary,
            backgroundColor: active ? styles.colors.warning : styles.colors.gray200,
            color: active ? styles.colors.gray900 : styles.colors.gray700,
            fontSize: styles.typography.fontSize.xs,
            padding: '0.3rem 0.625rem',
            whiteSpace: 'nowrap',
            flexShrink: 0,
        }}
        disabled={disabled}
        onClick={onClick}
        onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = active ? styles.colors.warning : styles.colors.gray300;
            e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = active ? styles.colors.warning : styles.colors.gray200;
            e.currentTarget.style.transform = 'translateY(0)';
        }}
    >
        {label}
    </button>
);

export default LeaseHeaderSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Header.tsx